import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Trash2, Edit, Calendar, Loader2 } from 'lucide-react';
import { draftApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Draft {
  draft_id: string;
  title: string;
  content: string;
  draft_type: string;
  created_at: string;
  updated_at: string;
  metadata?: any;
}

export default function DraftsList() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewDraftDialog, setShowNewDraftDialog] = useState(false);
  const [newDraftTitle, setNewDraftTitle] = useState('');
  const [newDraftType, setNewDraftType] = useState('general');
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirmDraftId, setDeleteConfirmDraftId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadDrafts();
  }, []);

  const loadDrafts = async () => {
    try {
      setIsLoading(true);
      const response = await draftApi.listDrafts();
      setDrafts(response.drafts || []);
    } catch (error) {
      console.error('Failed to load drafts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateDraft = async () => {
    if (!newDraftTitle.trim()) return;

    try {
      setIsCreating(true);
      const response = await draftApi.createDraft(newDraftTitle, '', newDraftType);
      setShowNewDraftDialog(false);
      setNewDraftTitle('');
      setNewDraftType('general');
      // Navigate to the new draft
      navigate(`/drafts/${response.draft_id}`);
    } catch (error) {
      console.error('Failed to create draft:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteDraft = async (draftId: string) => {
    try {
      setIsDeleting(true);
      await draftApi.deleteDraft(draftId);
      setDrafts(drafts.filter(d => d.draft_id !== draftId));
      setDeleteConfirmDraftId(null);
    } catch (error) {
      console.error('Failed to delete draft:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDraftTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'general': 'General',
      'specification': 'Specification',
      'claims': 'Claims',
      'background': 'Background',
      'summary': 'Summary',
      'detailed_description': 'Detailed Description'
    };
    return types[type] || type;
  };

  const getDraftTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'general': 'bg-gray-100 text-gray-800',
      'specification': 'bg-blue-100 text-blue-800',
      'claims': 'bg-purple-100 text-purple-800',
      'background': 'bg-green-100 text-green-800',
      'summary': 'bg-yellow-100 text-yellow-800',
      'detailed_description': 'bg-red-100 text-red-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Drafts</h1>
          <p className="text-gray-600 mt-1">Create and manage your patent drafts</p>
        </div>
        <Button onClick={() => setShowNewDraftDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Draft
        </Button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && drafts.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No drafts yet</h3>
            <p className="text-gray-600 mb-4">Create your first draft to get started</p>
            <Button onClick={() => setShowNewDraftDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Draft
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Drafts Grid */}
      {!isLoading && drafts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {drafts.map((draft) => (
            <Card key={draft.draft_id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{draft.title}</CardTitle>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded ${getDraftTypeColor(draft.draft_type)}`}>
                        {getDraftTypeLabel(draft.draft_type)}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteConfirmDraftId(draft.draft_id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-sm text-gray-600 line-clamp-3">
                    {draft.content || 'No content yet...'}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span>Updated {formatDate(draft.updated_at)}</span>
                  </div>
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => navigate(`/drafts/${draft.draft_id}`)}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Draft
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Draft Dialog */}
      <Dialog open={showNewDraftDialog} onOpenChange={setShowNewDraftDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Draft</DialogTitle>
            <DialogDescription>
              Start a new draft document for your patent work
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="draft-title">Draft Title</Label>
              <Input
                id="draft-title"
                placeholder="Enter draft title..."
                value={newDraftTitle}
                onChange={(e) => setNewDraftTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newDraftTitle.trim()) {
                    handleCreateDraft();
                  }
                }}
              />
            </div>
            <div>
              <Label htmlFor="draft-type">Draft Type</Label>
              <Select value={newDraftType} onValueChange={setNewDraftType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="specification">Specification</SelectItem>
                  <SelectItem value="claims">Claims</SelectItem>
                  <SelectItem value="background">Background</SelectItem>
                  <SelectItem value="summary">Summary</SelectItem>
                  <SelectItem value="detailed_description">Detailed Description</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDraftDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateDraft}
              disabled={!newDraftTitle.trim() || isCreating}
            >
              {isCreating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDraftId !== null} onOpenChange={() => setDeleteConfirmDraftId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Draft</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this draft? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmDraftId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmDraftId && handleDeleteDraft(deleteConfirmDraftId)}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
